/**
 * Helper function to print HTML content as PDF
 * Opens a new window with the HTML content and triggers print dialog
 * @param htmlContent - HTML string to print
 * @param options - Optional configuration
 */
export const printHtmlAsPdf = (htmlContent: string, options?: {autoPrint?: boolean}) => {
  try {
    const autoPrint = options?.autoPrint !== false; // Default to true

    // Create a new window with the HTML content
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Please allow popups to save PDF');
    }

    // Find background color from htmlContent
    const tempParser = document.createElement('div');
    tempParser.innerHTML = htmlContent;
    tempParser.style.position = 'fixed';
    tempParser.style.left = '-9999px';
    document.body.appendChild(tempParser);

    // Try to find background from body, table, or first element with background
    let foundBackground = '';
    const bodyElement = tempParser.querySelector('body') || tempParser;
    const computedStyle = window.getComputedStyle(bodyElement);
    const bgColor = computedStyle.backgroundColor;

    // Check inline styles or style attributes
    const bodyWithStyle = tempParser.querySelector('[style*="background"]') || tempParser.querySelector('body');
    if (bodyWithStyle && bodyWithStyle.getAttribute('style')) {
      const styleAttr = bodyWithStyle.getAttribute('style') || '';
      const bgMatch = styleAttr.match(/background(?:-color)?:\s*([^;]+)/i);
      if (bgMatch) {
        foundBackground = bgMatch[1].trim();
      }
    }

    // If no inline style, use computed background
    if (!foundBackground && bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
      foundBackground = bgColor;
    }

    // Also check for table or main container background
    if (!foundBackground) {
      const tableElement = tempParser.querySelector('table[bgcolor], table[style*="background"]');
      if (tableElement) {
        const tableBg =
          tableElement.getAttribute('bgcolor') ||
          tableElement.getAttribute('style')?.match(/background(?:-color)?:\s*([^;]+)/i)?.[1];
        if (tableBg) {
          foundBackground = tableBg.trim();
        }
      }
    }

    document.body.removeChild(tempParser);

    // Add print styles to preserve colors and backgrounds, remove margins
    const backgroundStyle = foundBackground ? `background: ${foundBackground} !important;` : '';
    const printStyles = `
      <style>
        @page {
          margin: 0 !important;
          padding: 0 !important;
          size: auto;
        }
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            ${backgroundStyle}
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        html, body {
          ${backgroundStyle}
        }
      </style>
    `;

    // Insert print script into htmlContent
    const printScript = autoPrint ? '<script>window.onload = function() { window.print(); };</script>' : '';
    let finalHtmlContent = htmlContent;

    // Try to insert styles in <head> or before </body>
    if (finalHtmlContent.includes('</head>')) {
      finalHtmlContent = finalHtmlContent.replace('</head>', `${printStyles}</head>`);
    } else if (finalHtmlContent.includes('<head>')) {
      finalHtmlContent = finalHtmlContent.replace('<head>', `<head>${printStyles}`);
    } else if (finalHtmlContent.includes('</body>')) {
      finalHtmlContent = finalHtmlContent.replace('</body>', `${printStyles}${printScript}</body>`);
    } else if (finalHtmlContent.includes('</html>')) {
      finalHtmlContent = finalHtmlContent.replace('</html>', `${printStyles}${printScript}</html>`);
    } else {
      // If no structure, wrap and add styles
      finalHtmlContent = `${printStyles}${finalHtmlContent}${printScript}`;
    }

    // Add script if not already added and autoPrint is enabled
    if (autoPrint && printScript && !finalHtmlContent.includes(printScript)) {
      if (finalHtmlContent.includes('</body>')) {
        finalHtmlContent = finalHtmlContent.replace('</body>', `${printScript}</body>`);
      } else if (finalHtmlContent.includes('</html>')) {
        finalHtmlContent = finalHtmlContent.replace('</html>', `${printScript}</html>`);
      } else {
        finalHtmlContent = `${finalHtmlContent}${printScript}`;
      }
    }

    printWindow.document.write(finalHtmlContent);
    printWindow.document.close();

    return printWindow;
  } catch (error) {
    console.error('Failed to print HTML as PDF:', error);
    throw error;
  }
};
